import React, { useState, useEffect } from 'react';
import { Filter, X, Calendar, Tag, User, AlertCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '../components/ui/dropdown-menu';

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'Hög', color: 'text-pink-400' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-400' },
  { value: 'low', label: 'Låg', color: 'text-blue-400' },
];

const TIME_OPTIONS = [
  { value: 'today', label: 'Idag' },
  { value: 'week', label: 'Denna vecka' },
  { value: 'month', label: 'Denna månad' },
  { value: 'all', label: 'Alla' },
];

export function TaskFilters({ 
  filters, 
  onFiltersChange, 
  tags = [],
  creators = []
}) {
  const activeFiltersCount = 
    (filters.priorities?.length || 0) + 
    (filters.tags?.length || 0) + 
    (filters.creators?.length || 0) + 
    (filters.timeRange !== 'all' ? 1 : 0);

  const clearFilters = () => {
    onFiltersChange({
      priorities: [],
      tags: [],
      creators: [],
      timeRange: 'all'
    });
  };

  const togglePriority = (priority) => {
    const current = filters.priorities || [];
    const updated = current.includes(priority)
      ? current.filter(p => p !== priority)
      : [...current, priority];
    onFiltersChange({ ...filters, priorities: updated });
  };

  const toggleTag = (tag) => {
    const current = filters.tags || [];
    const updated = current.includes(tag)
      ? current.filter(t => t !== tag)
      : [...current, tag];
    onFiltersChange({ ...filters, tags: updated });
  };

  const toggleCreator = (creator) => {
    const current = filters.creators || [];
    const updated = current.includes(creator)
      ? current.filter(c => c !== creator)
      : [...current, creator];
    onFiltersChange({ ...filters, creators: updated });
  };

  const setTimeRange = (range) => {
    onFiltersChange({ ...filters, timeRange: range });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="task-filters">
      {/* Priority Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={`border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8 ${
              filters.priorities?.length ? 'border-zinc-500 text-zinc-100' : ''
            }`}
            data-testid="filter-priority-btn"
          >
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
            Prioritet
            {filters.priorities?.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded bg-zinc-700 text-xs">
                {filters.priorities.length}
              </span>
            )}
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#141416] border-white/10">
          {PRIORITY_OPTIONS.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={filters.priorities?.includes(opt.value)}
              onCheckedChange={() => togglePriority(opt.value)}
              className={`${opt.color} cursor-pointer`}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Tag Filter */}
      {tags.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className={`border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8 ${
                filters.tags?.length ? 'border-zinc-500 text-zinc-100' : ''
              }`}
              data-testid="filter-tag-btn"
            >
              <Tag className="w-3.5 h-3.5 mr-1.5" />
              Tagg
              {filters.tags?.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-zinc-700 text-xs">
                  {filters.tags.length}
                </span>
              )}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#141416] border-white/10 max-h-64 overflow-y-auto">
            {tags.map((tag) => (
              <DropdownMenuCheckboxItem
                key={tag.id || tag.name}
                checked={filters.tags?.includes(tag.name)}
                onCheckedChange={() => toggleTag(tag.name)}
                className="cursor-pointer text-zinc-100"
              >
                <span className={`w-2 h-2 rounded-full bg-${tag.color}-400 mr-2`} />
                {tag.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Creator Filter */}
      {creators.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className={`border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8 ${
                filters.creators?.length ? 'border-zinc-500 text-zinc-100' : ''
              }`}
              data-testid="filter-creator-btn"
            >
              <User className="w-3.5 h-3.5 mr-1.5" />
              Skapare
              {filters.creators?.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-zinc-700 text-xs">
                  {filters.creators.length}
                </span>
              )}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#141416] border-white/10">
            {creators.map((creator) => (
              <DropdownMenuCheckboxItem
                key={creator}
                checked={filters.creators?.includes(creator)}
                onCheckedChange={() => toggleCreator(creator)}
                className="cursor-pointer text-zinc-100"
              >
                {creator}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Time Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={`border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8 ${
              filters.timeRange !== 'all' ? 'border-zinc-500 text-zinc-100' : ''
            }`}
            data-testid="filter-time-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            {TIME_OPTIONS.find(t => t.value === filters.timeRange)?.label || 'Tid'}
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#141416] border-white/10">
          {TIME_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => setTimeRange(opt.value)}
              className={`cursor-pointer ${filters.timeRange === opt.value ? 'text-zinc-100 bg-white/5' : 'text-zinc-400'}`}
            >
              {opt.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="text-zinc-500 hover:text-zinc-100 h-8"
          data-testid="clear-filters-btn"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Rensa ({activeFiltersCount})
        </Button>
      )}
    </div>
  );
}
